import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory, useParams } from 'react-router-dom';
import { fromJS } from 'immutable';
import { updateMobileSubscription } from 'flow/actions/extension-actions';
import Button from 'flow/components/ui-components/Button';
import Divider from 'flow/components/ui-components/Divider';
import Icon from 'flow/components/ui-components/Icon';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useMobile from 'flow/hooks/useMobile';
import {
    getAllSimcardsForExtension,
    getMobileExtension,
    hasMobileLicence,
} from 'flow/selectors/extension-selectors';
import { getUserCountryCode } from 'flow/selectors/selectors';
import { Simcard } from 'flow/types/Extension';
import { routes } from 'flow/types/routes';

import { TypedMap } from 'flow/types/TypedMap';
import { isInstalledEsim } from 'flow/utils/esim-utils';
import {
    getSimcardInformation,
    getSimCardStrings,
    hasAccessToEsimSettings,
    isActiveSimcard,
} from 'flow/utils/sim-card-utils';
import Page from './Page';
import SettingHeader from './SettingHeader';
import SettingRow from './SettingRow';
import { mobileParams } from './SingleSimcard';
import { generatePath } from 'flow/utils/router-utils';

const SimcardDetails = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { openModal } = useModal();
    const { simKey, extensionKey } = useParams<mobileParams>();

    const allSimcards = useAppSelector((state) =>
        getAllSimcardsForExtension(state, extensionKey),
    );
    const mobileExtension = useAppSelector((state) =>
        getMobileExtension(state, extensionKey),
    );
    const userHasMobileLicense = useAppSelector((state) =>
        hasMobileLicence(state, extensionKey),
    );
    const userCountryCode = useAppSelector((state) =>
        getUserCountryCode(state, extensionKey).toLowerCase(),
    );

    const { mobileSubscription } = useMobile(extensionKey);

    const simcard = allSimcards.find(
        (sim: TypedMap<Simcard>) => sim.get('key') === simKey,
    );

    const handleBlockSimcardClicked = (simcardKey: string) => {
        const updatedSubscription = fromJS({
            simcards: [
                {
                    key: simcardKey,
                    isBlocked: true,
                },
            ],
        });
        dispatch(updateMobileSubscription(extensionKey, updatedSubscription));
    };

    if (!mobileSubscription && !simcard) {
        return null;
    }

    const simcardInfo = getSimcardInformation(simcard, mobileSubscription);
    const eSimIsInstalled =
        simcardInfo.has('esimProfile') &&
        isInstalledEsim(simcardInfo.get('esimProfile'));
    const simCardStrings = getSimCardStrings(
        simcardInfo.has('esimProfile'),
        simcardInfo.get('isMainSimcard') === true,
        simcardInfo.get('connectedSimCards').size > 1,
    );
    const hasEsimAccess = hasAccessToEsimSettings(
        mobileSubscription,
        userHasMobileLicense,
        userCountryCode,
    );

    const isAdditionalIdentification =
        simcard.get('imsi') ||
        simcard.get('icc') ||
        mobileExtension.get('phoneModel');

    const blockedDescription = (
        <>
            <div
                className="mb-3"
                dangerouslySetInnerHTML={{
                    __html: i18next.t(simCardStrings.reactivateHelpText),
                }}
            />

            {i18next.t(simCardStrings.confirmationText)}
        </>
    );

    const handleConfirmation = () => {
        openModal('confirmationDialog', {
            title: i18next.t(simCardStrings.blockTitleText),
            description: blockedDescription,
            handleAccept: () =>
                handleBlockSimcardClicked(simcardInfo.get('key')),
        });
    };

    return (
        <Page
            header="settings.simcard_details"
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.mobile.simKey'], {
                        extensionKey,
                        simKey,
                    }),
                )
            }
        >
            {!isActiveSimcard(simcard) ? (
                <SettingRow>{i18next.t('settings.no_simcards')}</SettingRow>
            ) : (
                <>
                    {simcardInfo.get('isBlocked') ? (
                        <>
                            <p className="mb-3 font-bold">
                                {i18next.t('settings.blocked_card')}
                            </p>
                            <div
                                className="text-sm text-gray-600"
                                dangerouslySetInnerHTML={{
                                    __html: i18next.t(
                                        simCardStrings.reactivateHelpText,
                                    ),
                                }}
                            />
                        </>
                    ) : (
                        <>
                            <SettingRow
                                cta={
                                    <p className="text-gray-600">
                                        {simcard.get('puk1')}
                                    </p>
                                }
                            >
                                {i18next.t('setting.puk')}
                            </SettingRow>
                            <SettingRow
                                cta={
                                    <p className="text-gray-600">
                                        {simcard.get('pin1')}
                                    </p>
                                }
                            >
                                {i18next.t('settings.voicemail_pin')}
                            </SettingRow>
                            <div className="ms-2 text-sm text-gray-600">
                                {i18next.t('setting.pin_help_text')}
                            </div>
                            {isAdditionalIdentification ? <Divider /> : null}

                            <div className="my-3">
                                {simcard.get('imsi') ? (
                                    <SettingRow
                                        cta={
                                            <p className="whitespace-nowrap text-gray-600">
                                                {simcard.get('imsi')}
                                            </p>
                                        }
                                    >
                                        {i18next.t('settings.simcard_imsi')}
                                    </SettingRow>
                                ) : null}
                                {simcard.get('icc') ? (
                                    <SettingRow
                                        cta={
                                            <p className="whitespace-nowrap text-gray-600">
                                                {simcard.get('icc')}
                                            </p>
                                        }
                                    >
                                        {i18next.t('settings.simcard_icc')}
                                    </SettingRow>
                                ) : null}
                                {mobileExtension.get('phoneModel') ? (
                                    <SettingRow
                                        cta={
                                            <p className="whitespace-nowrap text-gray-600">
                                                {mobileExtension.get(
                                                    'phoneModel',
                                                )}
                                            </p>
                                        }
                                    >
                                        {i18next.t('settings.mobile_phone')}
                                    </SettingRow>
                                ) : null}
                            </div>
                            <Divider />
                            <SettingHeader>
                                {i18next.t('settings.simcard_cardType')}
                            </SettingHeader>

                            <SettingRow
                                cta={
                                    hasEsimAccess &&
                                    (!simcardInfo.has('esimProfile') ||
                                        !eSimIsInstalled) &&
                                    simcardInfo.get('isMainSimcard') ===
                                        true ? (
                                        <Button
                                            onClick={() =>
                                                history.push(
                                                    generatePath(
                                                        routes[
                                                            'settings.mobile.esim'
                                                        ],
                                                        { extensionKey },
                                                    ),
                                                )
                                            }
                                        >
                                            <p className="whitespace-nowrap text-gray-600">
                                                {i18next.t(
                                                    'settings.esim.replace',
                                                )}
                                            </p>
                                        </Button>
                                    ) : null
                                }
                            >
                                <div className="flex items-center">
                                    <Icon iconKey="simCard" className="me-3" />
                                    {simcardInfo.has('esimProfile')
                                        ? i18next.t('settings.esim')
                                        : i18next.t('settings.simcard')}
                                </div>
                            </SettingRow>
                            <Divider />
                            <SettingHeader>
                                {i18next.t('settings.block_simcard')}
                            </SettingHeader>
                            <div className="mb-3 flex items-center justify-between">
                                <div className="text-sm text-gray-600">
                                    {i18next.t(simCardStrings.blockHelpText)}
                                </div>
                                <Button
                                    color="ghost"
                                    onClick={handleConfirmation}
                                >
                                    {i18next.t('settings.block_simcard')}
                                </Button>
                            </div>
                        </>
                    )}
                </>
            )}
        </Page>
    );
};

export default SimcardDetails;
